import React, { ButtonHTMLAttributes, useId } from 'react';
import { useCanPerform, useWriteGuard, WriteCapabilityLevel } from '../contexts/WriteGuardContext';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    /** `link`: text in the link colour, no box — an action that reads as a
     *  link («Ver todos», «Categorizar»). It ignores `size`. */
    variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'success' | 'warning' | 'link';
    /**
     * Mark this button as a write action gated by the app's role. When set and
     * the current user lacks the capability (see {@link WriteGuardProvider}),
     * the button auto-disables and shows the read-only tooltip — no per-call
     * `disabled={!canWrite}` wiring. Omit it for benign buttons (cancel, close,
     * filter, navigation) so they stay usable for read-only users.
     *   - `'write'`: requires `canWrite` (create/update/delete business data).
     *   - `'config'`: requires `canConfigure` (settings / configuration).
     */
    requires?: WriteCapabilityLevel;
    /**
     * Visual sizes:
     * - `'sm'`: compact chip, `px-2 py-1`, tiny font — use inside dense tables / inline.
     * - `'md'` (default): **regular button**, `h-10` — matches the height of `Input`
     *   so it aligns perfectly in toolbars, sticky headers, forms, and modal actions.
     *   This is the DEFAULT and covers ~95% of the app.
     * - `'lg'`: **page-header primary CTA**, `h-14` — only for the "hero" action
     *   rendered directly under the `PageHeader` title, typically next to a
     *   `DropZoneButton` (e.g. "Nuevo pedido" on the Dashboard, "Nuevo diseño"
     *   on the Designs page). Matches `DropZoneButton`'s height so both align.
     *
     * See `documentation/Standards.md` → "Button sizes".
     */
    size?: 'sm' | 'md' | 'lg';
    icon?: IconName;
    /** When true, shows a spinning ``progress_activity`` icon (replacing ``icon``)
     *  and disables the button. Use for async actions (publish/import/save). */
    loading?: boolean;
    /** When true, flips the button to the green "success" state: a check icon
     *  (see {@link successIcon}) plus a brief pulse. This is the visual half of
     *  the spin→check feedback. Wins over ``loading`` and ``icon``.
     *
     *  How to drive it, by situation:
     *   - **Async action** → don't set this directly; use {@link ActionButton}
     *     (`onAction`) which manages idle→spin→green for you.
     *   - **Form save** → {@link SaveButton} (tenant) sets it internally; for the
     *     admin `<Button type="submit">` pattern, pair it with the `useSavedPulse`
     *     hook (`success={saved}`, call `markSaved()` after a successful save).
     *   - **Manual** → set it true yourself and clear it after a timeout. */
    success?: boolean;
    /** Icon rendered while ``success`` is true (default ``check``). */
    successIcon?: IconName;
    /** Square, label-less button (no children). Always pair with `aria-label`. */
    iconOnly?: boolean;
    className?: string;
}

const Button: React.FC<ButtonProps> = ({
    children,
    variant = 'primary',
    size = 'md',
    icon,
    loading = false,
    success = false,
    successIcon = 'check',
    iconOnly = false,
    className = '',
    disabled,
    requires,
    title,
    ...props
}) => {
    // Role gate (Capa 1/2 of the read-only standardization). When `requires` is
    // unset this is a no-op and the button behaves exactly as before — so the
    // hundreds of existing buttons and the admin/web apps (no provider mounted)
    // are unaffected. Hooks run unconditionally to satisfy the rules of hooks;
    // they read the permissive default when no provider is present.
    const blockedByRole = !useCanPerform(requires ?? 'write') && requires !== undefined;
    const { message: readOnlyMessage } = useWriteGuard();
    const effectiveDisabled = disabled || blockedByRole;
    const effectiveTitle = blockedByRole ? (title ?? readOnlyMessage) : title;
    // `disabled:pointer-events-none` means the `title` can never show as a
    // tooltip on a role-blocked button; the reason is also rendered as
    // screen-reader-only text and linked through aria-describedby.
    const reasonId = useId();
    const describedBy = [props['aria-describedby'], blockedByRole && effectiveTitle ? reasonId : null].filter(Boolean).join(' ') || undefined;

    // `motion-safe:` — the scale flourish is skipped for people who asked the
    // OS to reduce motion; base.css also collapses the transition itself.
    const baseClasses = "font-bold transition-all inline-flex items-center justify-center gap-2 whitespace-nowrap shrink-0 motion-safe:active:scale-95 motion-safe:hover:scale-[1.02] disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus-visible:outline-none";

    // Variant styles
    const variants = {
        primary: "bg-primary hover:bg-primary-dark text-white rounded-xl shadow-lg shadow-primary/20",
        secondary: "bg-white dark:bg-surface-dark border border-slate-300 dark:border-slate-600 hover:border-primary text-primary shadow-sm rounded-xl",
        danger: "bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-500/20 rounded-xl",
        ghost: "bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl",
        // Solid semantic-action skins (distinct from the transient ``success`` PULSE
        // prop above): green = positive/confirm (e.g. activate a draft), amber = needs
        // attention (e.g. sync/review).
        success: "bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl",
        warning: "bg-amber-500 hover:bg-amber-600 text-white rounded-xl",
        link: "rounded-md font-display font-semibold text-primary-dark hover:text-primary hover:underline underline-offset-2 dark:text-primary-light dark:hover:text-primary focus-visible:ring-2 focus-visible:ring-primary/50",
    };

    // Size styles
    const sizes = {
        sm: "px-2 py-1 text-[11px] uppercase tracking-wider",
        md: "h-10 px-4 text-sm",
        lg: "h-14 px-5 text-base"
    };

    /* Square, label-less button. Before this prop the same look was reached
     * with three incompatible `className` overrides across the app
     * (`!px-0 !w-10 !rounded-lg`, `!h-auto !px-3 !py-1.5`, `!h-auto !px-2 !py-1`).
     * Pair with `aria-label` — there is no text to read out. */
    const iconOnlySizes = {
        sm: "!px-0 w-7 h-7",
        md: "!px-0 w-10",
        lg: "!px-0 w-14",
    };

    // Calculate icon size based on button size
    const iconSizes = {
        sm: "!text-[16px]",
        md: "!text-[18px]",
        lg: "!text-[20px]"
    };

    // Green success skin — shared with SaveButton. ``!`` wins over the variant bg
    // AND text colour: ``!text-white`` is required so non-primary variants (e.g. the
    // secondary ActionButton, whose idle text is ``text-primary`` orange) don't show
    // orange text on the green pulse. Primary already has white text, so this is a
    // no-op there — it just keeps every variant's success state legible.
    const successOverride = success
        ? '!bg-green-500 hover:!bg-green-500 !text-white !shadow-green-500/30 animate-save-success-pulse'
        : '';

    const isLink = variant === 'link';
    const finalClassName = isLink
        ? `font-semibold inline-flex items-center gap-1 whitespace-nowrap text-xs disabled:opacity-50 disabled:pointer-events-none focus:outline-none ${variants.link} ${className}`.replace(/\s+/g, ' ').trim()
        : `${baseClasses} ${variants[variant]} ${sizes[size]} ${iconOnly ? iconOnlySizes[size] : ''} ${successOverride} ${className}`.replace(/\s+/g, ' ').trim();

    return (
        <button className={finalClassName} disabled={effectiveDisabled || loading} title={effectiveTitle} {...props} aria-describedby={describedBy} aria-busy={loading || undefined}>
            {success ? (
                <Icon name={successIcon} className={`${iconSizes[size]}`} aria-hidden="true" />
            ) : loading ? (
                <Icon name="progress_activity" className={`animate-spin ${iconSizes[size]}`} aria-hidden="true" />
            ) : icon ? (
                <Icon name={icon} className={`${iconSizes[size]}`} aria-hidden="true" />
            ) : null}
            {children}
            {blockedByRole && effectiveTitle ? <span id={reasonId} className="sr-only">{effectiveTitle}</span> : null}
        </button>
    );
};

export default Button;
